import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useStaff } from '../contexts/StaffContext';
import { hasPermission, StaffPermission } from '../utils/rolePermissions';
import Spinner from './Spinner';

interface StaffProtectedRouteProps {
  children: React.ReactElement;
  permission?: StaffPermission;
}

const StaffProtectedRoute: React.FC<StaffProtectedRouteProps> = ({ children, permission }) => {
  const { staff, isAuthenticated, isLoading } = useStaff();
  const location = useLocation();
  const navigate = useNavigate();

  if (isLoading) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/staff/login" state={{ from: location }} replace />;
  }

  if (permission && staff) {
    const allowed = staff.permissions?.[permission] ?? hasPermission(staff.role, permission);
    if (!allowed) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Access denied
          </Typography>
          <Typography sx={{ mb: 2 }}>
            You do not have permission to access this staff page.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/staff/dashboard')}>
            Back to dashboard
          </Button>
        </Box>
      );
    }
  }

  return children;
};

export default StaffProtectedRoute;
